/**
 * ELM327 protocol helpers — command strings + response cleaning. Transport
 * (Web Bluetooth GATT) lives in the PWA; this module is pure string/byte work
 * so it can be unit-tested without hardware and shared across surfaces.
 */
/** Carriage return terminates every ELM327 command. */
export const ELM327_TERMINATOR = '\r';
/**
 * Adapter initialization sequence. Sent once after GATT connect, in order.
 * Delays are conservative; ATZ (full reset) needs the longest settle.
 *
 * - ATZ   full reset
 * - ATE0  echo off (don't echo our own command back)
 * - ATL0  linefeeds off
 * - ATS0  spaces off (compact responses; cleaner parses either way)
 * - ATH0  headers off (we parse bare service responses)
 * - ATSP0 automatic protocol detection (CAN on modern cars)
 */
export const ELM327_INIT_SEQUENCE = [
    { cmd: 'ATZ', delayMs: 1000 },
    { cmd: 'ATE0', delayMs: 120 },
    { cmd: 'ATL0', delayMs: 120 },
    { cmd: 'ATS0', delayMs: 120 },
    { cmd: 'ATH0', delayMs: 120 },
    { cmd: 'ATSP0', delayMs: 120 },
];
/** Service-mode command builders. */
export const CMD_STORED_DTCS = '03';
export const CMD_PENDING_DTCS = '07';
export const CMD_CLEAR_DTCS = '04';
export const CMD_READINESS = '0101'; // Mode 01 PID 01
export const CMD_VIN = '0902'; // Mode 09 PID 02
export function buildPidCommand(pid) {
    return `01${pid}`;
}
/** ELM327 status words that are not data and must never be parsed as hex. */
const STATUS_WORD = /(SEARCHING|BUS\s*INIT|BUSINIT|STOPPED|UNABLE\s*TO\s*CONNECT|CAN\s*ERROR|BUFFER\s*FULL|RX\s*ERROR|<DATA\s*ERROR>|ERR\d+)/i;
/** Returned by `cleanElmResponse` callers when the ECU has nothing to report. */
export function isNoData(raw) {
    return /\bNO\s*DATA\b/i.test(raw) || /\bUNABLE\s*TO\s*CONNECT\b/i.test(raw);
}
/**
 * Normalize a raw ELM327 reply into an array of data bytes.
 *
 * Handles: command echo lines, the `>` prompt, status words, ISO-TP multi-frame
 * markers (`0:`, `1:`), the multi-frame length token (a lone 3-hex-char line),
 * and both spaced (`41 0C 1A F8`) and compact (`410C1AF8`) formats.
 *
 * Returns [] for NO DATA / status-only replies — callers treat that as
 * "no codes" / "PID unsupported" rather than an error.
 */
export function cleanElmResponse(raw) {
    const lines = raw
        .split(/[\r\n]+/)
        .map((l) => l.trim())
        .filter(Boolean);
    const hexParts = [];
    for (let line of lines) {
        if (line === '>' || line.toUpperCase() === 'OK')
            continue;
        if (STATUS_WORD.test(line) || /\bNO\s*DATA\b/i.test(line))
            continue;
        // Strip ISO-TP frame index marker, e.g. "0:" / "1:".
        line = line.replace(/^[0-9A-Fa-f]:\s*/, '');
        // A lone 3-hex-char line is the multi-frame total-length token — drop it.
        // (Real single-frame responses are always an even number of hex chars >= 4.)
        if (/^[0-9A-Fa-f]{3}$/.test(line))
            continue;
        hexParts.push(line);
    }
    const hex = hexParts.join('').replace(/[^0-9A-Fa-f]/g, '');
    const bytes = [];
    for (let i = 0; i + 1 < hex.length; i += 2) {
        bytes.push(parseInt(hex.slice(i, i + 2), 16));
    }
    return bytes;
}
/**
 * Decode a Mode 09 PID 02 (VIN) response into the ASCII VIN, or null.
 * The response is `49 02 01 <17 ascii bytes>`; some adapters interleave a
 * frame/record counter (the `01`) which we skip past to the printable run.
 */
export function decodeVinResponse(bytes) {
    if (bytes[0] !== 0x49 || bytes[1] !== 0x02)
        return null;
    // Collect printable ASCII (alphanumeric) from the payload after the header.
    const chars = [];
    for (let i = 2; i < bytes.length; i++) {
        const c = bytes[i];
        if (c === undefined)
            continue;
        if (c >= 0x30 && c <= 0x5a)
            chars.push(String.fromCharCode(c)); // 0-9, A-Z
    }
    const vin = chars.join('');
    return vin.length >= 11 ? vin.slice(-17) : null;
}
//# sourceMappingURL=elm327.js.map